import { Routes } from '@angular/router';
import { AuthGuard } from './core/auth/guards/auth/auth.guard';
import { RoleGuard } from './core/auth/guards/role/role.guard';
import { Roles } from './core/enums/roles.enum';
import { AuthenticatedGuard } from './core/auth/guards/authenticated/authenticated.guard';
import { environment } from '../environments/environment';
import { AppRoutes } from './shared/route-paths.const';
import ClientesComponent from './clientes/components/clientes/clientes.component';
import { CONTABILIDAD_ONDA_ROUTES } from './contabilidad-onda/contabilidad-onda.routes';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./shared/components/layout/layout.component'),
    children: [
      // HOME
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        canActivate: [AuthGuard],
        loadComponent: () => import('./shared/components/dashboard/dashboard.component'),
      },
      {
        path: 'admin',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () => import('./home/components/home-admin/home-admin.component'),
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'home',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () => import('./home/components/home-user/home-user.component'),
        data: { roles: [Roles.Representante, Roles.Usuario] },
      },
      // CONTABILIDAD-ONDA
      ...(environment.features.contabilidadOnda
        ? [
            {
              path: AppRoutes.CONTABILIDAD_ONDA.ROOT,
              children: CONTABILIDAD_ONDA_ROUTES,
            },
          ]
        : []),
      // CLIENTES
      {
        path: 'clientes',
        component: ClientesComponent,
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'clientes/:id',
        loadComponent: () => import('./clientes/components/cliente/cliente.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      // SUMINISTROS
      {
        path: 'suministros',
        loadComponent: () => import('./suministros/components/suministros/suministros.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'suministros/:id',
        loadComponent: () => import('./suministros/components/suministro/suministro.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      // CONTRATOS
      {
        path: 'contratos',
        loadComponent: () => import('./contratos/components/contratos/contratos.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'mis-contratos',
        loadComponent: () => import('./contratos/components/mis-contratos/mis-contratos.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.Representante, Roles.Usuario] },
      },
      {
        path: 'gestionar-contratos',
        loadComponent: () => import('./contratos/components/gestionar-contratos/gestionar-contratos.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.Representante] },
      },
      {
        path: 'contratos/:id',
        loadComponent: () => import('./contratos/components/contrato/contrato.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin, Roles.Representante, Roles.Usuario] },
      },
      {
        path: 'registro-contrato',
        loadComponent: () => import('./contratos/components/registro-contrato/registro-contrato.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      // FACTURAS
      {
        path: 'filtrar-facturas',
        loadComponent: () => import('./facturas/components/filtrar-facturas/filtrar-facturas.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'facturas-suministros',
        loadComponent: () =>
          import('./facturas/components/facturas-suministro/facturas-suministro.component').then(
            (m) => m.FacturasSuministroComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'facturas-pendientes-envio',
        loadComponent: () =>
          import('./facturas/components/facturas-pendientes-envio/facturas-pendientes-envio.component').then(
            (m) => m.FacturasPendientesEnvioComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'mis-facturas-suministros',
        loadComponent: () =>
          import('./facturas/components/mis-facturas-suministro/mis-facturas-suministro.component').then(
            (m) => m.MisFacturasSuministroComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.Representante, Roles.Usuario] },
      },
      {
        path: 'facturas-suministros/:idFacturaSuministro',
        loadComponent: () =>
          import('./facturas/components/factura-suministro/factura-suministro.component').then(
            (m) => m.FacturaSuministroComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin, Roles.Representante, Roles.Usuario] },
      },
      {
        path: 'facturas-suministros/:idFacturaSuministro/editar',
        loadComponent: () =>
          import('./facturas/components/editar-factura-suministro/editar-factura-suministro.component').then(
            (m) => m.EditarFacturaSuministroComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'facturas-suministros/:idFacturaSuministro/detalle',
        loadComponent: () =>
          import('./facturas/components/editar-factura-suministro/editar-factura-suministro.component').then(
            (m) => m.EditarFacturaSuministroComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'facturas-otros-conceptos',
        loadComponent: () =>
          import('./facturas/components/facturas-otros-conceptos/facturas-otros-conceptos.component').then(
            (m) => m.FacturasOtrosConceptosComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'mis-facturas-otros-conceptos',
        loadComponent: () =>
          import('./facturas/components/mis-facturas-otros-conceptos/mis-facturas-otros-conceptos.component').then(
            (m) => m.MisFacturasOtrosConceptosComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.Representante, Roles.Usuario] },
      },
      {
        path: 'facturas-otros-conceptos/:idFacturaOtrosConceptos',
        loadComponent: () =>
          import('./facturas/components/factura-otros-conceptos/factura-otros-conceptos.component').then(
            (m) => m.FacturaOtrosConceptosComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin, Roles.Representante, Roles.Usuario] },
      },
      {
        path: 'facturas-otros-conceptos/:idFacturaOtrosConceptos/editar',
        loadComponent: () =>
          import('./facturas/components/editar-factura-conceptos/editar-factura-conceptos.component').then(
            (m) => m.EditarFacturaConceptosComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'contratos/:idContrato/nueva-factura',
        loadComponent: () =>
          import('./facturas/components/nueva-factura/nueva-factura.component').then((m) => m.NuevaFacturaComponent),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'contratos/:idContrato/nueva-factura-conceptos',
        loadComponent: () =>
          import('./facturas/components/nueva-factura-conceptos/nueva-factura-conceptos.component').then(
            (m) => m.NuevaFacturaConceptosComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      // REPRESENTANTES
      {
        path: 'representantes',
        loadComponent: () => import('./representantes/components/representantes/representantes.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'representantes/nuevo',
        loadComponent: () => import('./representantes/components/nuevo-representante/nuevo-representante.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'representantes/:id',
        loadComponent: () => import('./representantes/components/representante/representante.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      // USUARIOS
      {
        path: 'usuarios',
        loadComponent: () => import('./usuarios/components/usuarios/usuarios.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      {
        path: 'usuarios/:id',
        loadComponent: () => import('./usuarios/components/usuario/usuario.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin, Roles.Admin] },
      },
      // MAESTROS
      {
        path: 'maestros',
        loadComponent: () => import('./maestros/components/dashboard/dashboard.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/cuentas-bancarias',
        loadComponent: () => import('./maestros/components/cuenta-bancaria/cuenta-bancaria.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/estados-compra',
        loadComponent: () => import('./maestros/components/estado-compra/estado-compra.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/metodos-pago',
        loadComponent: () => import('./maestros/components/metodo-pago/metodo-pago.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/tipo-documento',
        loadComponent: () => import('./maestros/components/tipo-documento/tipo-documento.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/tipo-iva',
        loadComponent: () => import('./maestros/components/tipo-iva/tipo-iva.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/tipo-via',
        loadComponent: () => import('./maestros/components/tipo-via/tipo-via.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/tipo-lectura',
        loadComponent: () => import('./maestros/components/tipo-lectura/tipo-lectura.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/estado-contrato',
        loadComponent: () => import('./maestros/components/estado-contrato/estado-contrato.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/empresa-facturadora',
        loadComponent: () => import('./maestros/components/empresa-facturadora/empresa-facturadora.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/canales',
        loadComponent: () => import('./maestros/components/canal/canal.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/tarifas',
        loadComponent: () => import('./maestros/components/tarifa/tarifa.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/proveedores',
        loadComponent: () => import('./maestros/components/proveedor/proveedor.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/impuestos',
        loadComponent: () => import('./maestros/components/impuesto/impuesto.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/modalidades',
        loadComponent: () => import('./maestros/components/modalidad/modalidad.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'maestros/estado-rentabilidad',
        loadComponent: () => import('./maestros/components/estado-rentabilidad/estado-rentabilidad.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
      {
        path: 'utilidades/visor-norma',
        loadComponent: () => import('./utils/components/visor-norma/visor-norma.component'),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: [Roles.SuperAdmin] },
      },
    ],
  },
  {
    path: 'login',
    loadComponent: () => import('./core/components/login/login.component'),
    canActivate: [AuthenticatedGuard],
  },
  { path: '**', redirectTo: '/' },
];
